import React, { ReactElement, useState, useRef, useEffect, useCallback, useMemo } from 'react';

import cls from 'classnames';

import _isBoolean from 'lodash/isBoolean';
import _noop from 'lodash/noop';

import ChevronDownIcon from '@ott/icon-chevron-down';
import CrossIcon from '@ott/icon-cross';
import { useLayoutContext, determineLayoutContext } from '@ott/layout-context';
import Modal from '@ott/modal';

import { SelectOption } from './blocks/Option/Option';
import Overlay from './blocks/Overlay';
import { ALIGN_TYPES, PLACEMENT, SELECT_MODES, SIZE } from './helpers/constants';
import { KEY_VALUES, KEYS } from './helpers/constants';
import { isOptionGroup, isSingleOnChange, getParsedSelectOptions } from './helpers/helpers';
import {
  Modes,
  MultipleOnChange,
  SelectMultipleValue,
  SingleMode,
  SingleOnChange,
  SelectSingleValue,
  Size,
  AlignType,
} from './helpers/types';

import styles from './Select.scss';

export type { SelectOption } from './blocks/Option/Option';

export type SelectCustomRenderer<T> = (props: T) => ReactElement;

export type SelectValue = string | number;

export type OptionGroupProps = {
  label: string;
  isOptionGroup: boolean;
  className?: string;
};

export type SelectClosedBy = {
  enter?: boolean;
  escape?: boolean;
  space?: boolean;
  tab?: boolean;
  outsideClick?: boolean;
  triggerClick?: boolean;
  outerControl?: boolean;
  select?: boolean;
};

export type CustomTriggerProps<TriggerProps extends object = Record<string, never>> = {
  /** Функция чтобы закрыть оверлей */
  closeOverlay: () => void;
  /** Функция чтобы открыть оверлей */
  openOverlay: () => void;
  /** Текущее значение инпута */
  value: SelectValue | null | SelectValue[];
  /** Реф который нужно прокинуть в кастомный триггер ОБЯЗАТЕЛЬНО,
   * если вам нужно поставить еще и свой реф, то делайте так
   * ref={(node => {
   *   setRef.current = node;
   *   inputRef.current = node;
   * })}
   * */
  setRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Состояние оверлея */
  isOverlayOpen: boolean;
  /** Генерик, сюда полетят все что вы прокините в customTriggerProps
   * Прокидывайте пропы в рендерер только таким образом,
   * потому что ФУНКЦИЯ РЕНДЕРЕРА ДОЛЖНА БЫТЬ КОНСТАНТНА
   * */
  triggerProps: TriggerProps;
};

export type CustomOptionProps<Option extends SelectOption = SelectOption> = {
  /** Сюда будет лететь опшен из тех которые вы прокидываете */
  option: Option;
  /** Выбран ли этот опшен */
  isActive: boolean;
  /** Заховерен ли опшен, используйте этот проп, а не :hover,
   * иначе initial ховер и ховер по стрелкам будет не работать */
  isHovered: boolean;
};

type BaseProps<
  Option extends SelectOption,
  TriggerProps extends object = {},
  Mode extends Modes = SingleMode
> = {
  /** То как будет вести себя селект, multiple - мультивыбор опций, single - выбор только одной опции */
  mode?: Mode;
  /** Класс контейнера селекта */
  className?: string;
  /** Классы внутренних элементов */
  childrenClassNames?: {
    inputWrapper?: string;
    trigger?: string;
    overlayContent?: string;
    triggerWrapper?: string;
    inputContent?: string;
    label?: string;
    optionWrapper?: string;
    arrow?: string;
    contentWrapper?: string;
  };
  /** Лейбл который в спокойном состоянии отображен как плейсхолдер, а в активном уходит в левый верхний угол */
  label?: string;
  /** Если false, то label в активном состоянии исчезнет*/
  withTitle?: boolean;
  /** Как оверлей показывается относительно триггера,
   * triggerWidth - оверлей будет по ширине всего триггера */
  alignType?: AlignType;
  /** Варианта которые будут отображаться в дропдауне */
  options?: (Option | OptionGroupProps)[];
  /** Нейм необходимый при использовании react hook form и FirstErrorShower'а */
  name?: string;
  /** Если вы используете rhf и FirstErrorShower'а вместе с fullscreenOnMobile,
   * т.к. модалка рендерится в портале, то инпут не будет в HTMLFormElement.elements,
   * это нужно чтобы сказать форме что этот селект тоже принадлежит ей, без этого будет некорректно работать FES */
  form?: string;
  /** Кастомный локатор */
  specificLocator?: string;
  /** Колбэк вызывающийся при нажатии на кнопку ресета */
  onReset?: () => void;
  /** Если фолс, то при открытии оверлея ховер не будет проставляться на активный
   *  опшен (если он есть) или на первый встречный */
  shouldSetInitialHover?: boolean;
  /** С этим пропом автоматическое изменение расположения выключиться и высота будет обрезаться по экрану */
  shouldLimitHeightByScreen?: boolean;
  /** Проп отвечающий за отображение кнопки ресета */
  withResetButton?: boolean;
  /** Размер триггера и вариантов выбора */
  size?: Size;
  /** Задизейблен ли триггер */
  isDisabled?: boolean;
  /** Если true, то оверлей всегда будет снизу, нужно в случаях когда есть тултип,
   *  не нужно включать если включен shouldLimitHeightByScreen */
  isVerticalAutoPlacementDisabled?: boolean;
  /** Использовать ли виртуализацию для рендера списка опций,
   * для использования все опции должны быть строго одной высоты */
  isVirtualized?: boolean;
  /** Используется вместе с isVirtualized, нужно если у нас кастомная высота опций,
   *  иначе высота будет отталкиваться от size */
  virtualizationRowHeight?: number;
  /** Колбэк срабатывающий по открытию дропдауна */
  onOpen?: () => void;
  /** Колбэк срабатывающий по закрытию дропдауна */
  onClose?: (closedBy: SelectClosedBy) => void;
  /** Колбэк срабатывающий по блюру контейнера */
  onBlur?: () => void;
  /** Колбэк срабатывающий по клику на триггер */
  onTriggerClick?: () => void;
  /** Нужно ли красить бордер в красный, как правило для отображения ошибок */
  isInvalid?: boolean;
  /** Открыт ли оверлей, для управления состоянием оверлея из вне */
  isOpen?: boolean;
  /** Проп отвечающий за отображение селекта в модалке на весь экран в адаптиве */
  fullscreenOnMobile?: boolean;
  /** Тайтл для модалки в адаптиве с пропом fullscreenOnMobile = true */
  modalTitle?: string;
  /** Кастомный рендер опшена в оверлее */
  CustomOptionRenderer?: SelectCustomRenderer<CustomOptionProps<Option>>;
  /** Пропсы для кастомного триггера */
  customTriggerProps?: TriggerProps;
  /** Кастомный рендер триггера */
  CustomTriggerRenderer?: SelectCustomRenderer<CustomTriggerProps<TriggerProps>>;
  // возможно сможем типизизировать конкретней после перехода на 17-18 реакт,
  // ибо там должны были поменять рантайм jsx,
  // сейчас рантайм оборачивает компоненты в JSX.Element поэтому сделать чтобы можно было
  // прокидывать только Option и OptionGroup не получится
  /** Можем прокидывать опшены так же и через jsx вариант, принимает только Option и OptionGroup */
  children?: ReactElement | ReactElement[] | null | false;
  /** Контейнер в котором будет рендерится оверлей */
  container?: null | HTMLElement;
} & MultipleOptionsTitle;

type MultipleOptionsTitle = {
  /** Значение которое будет отображаться в триггере при mode === multiple, иначе будет плейсхолдер */
  multipleOptionsTitle?: string;
};

type ModeProps<Mode extends Modes, Option extends SelectOption> = {
  /** Текущее значение селекта */
  value: Mode extends SingleMode ? SelectSingleValue : SelectMultipleValue;
  /** Колбэк срабатывающий при выборе варианта из дропдауна */
  onChange: Mode extends SingleMode ? SingleOnChange<Option> : MultipleOnChange<Option>;
};

// без кондишионала будет ругаться
// '"single"' is assignable to the constraint of type 'Mode', but 'Mode'
// could be instantiated with a different subtype of constraint 'Modes'.

export type SelectProps<
  Option extends SelectOption = SelectOption,
  TriggerProps extends object = {},
  Mode extends Modes = SingleMode
> = Mode extends SingleMode
  ? BaseProps<Option, TriggerProps, Mode> & ModeProps<Mode, Option>
  : BaseProps<Option, TriggerProps, Mode> &
      ModeProps<Mode, Option> &
      Required<MultipleOptionsTitle>;

function Select<
  Option extends SelectOption,
  TriggerProps extends object,
  Mode extends Modes = SingleMode
>({
  // core
  value,
  options,
  specificLocator = 'select',
  isOpen,
  name,
  form,
  mode = SELECT_MODES.SINGLE,
  isDisabled = false,
  withResetButton = false,
  fullscreenOnMobile = false,
  modalTitle,
  children,
  container,

  // callbacks
  onChange,
  onReset = _noop,
  onTriggerClick = _noop,
  onOpen = _noop,
  onClose = _noop,
  onBlur = _noop,

  // custom renderers
  CustomOptionRenderer,
  CustomTriggerRenderer,
  customTriggerProps = {} as TriggerProps,

  // trigger texts
  label,
  multipleOptionsTitle,

  // visualization
  className,
  withTitle = !!label,
  childrenClassNames = {},
  shouldLimitHeightByScreen = false,
  shouldSetInitialHover = !determineLayoutContext().isMobile,
  isVerticalAutoPlacementDisabled = false,
  isVirtualized = false,
  virtualizationRowHeight,
  alignType = ALIGN_TYPES.LEFT,
  size = SIZE.DEFAULT,
  isInvalid,
}: SelectProps<Option, TriggerProps, Mode>) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [isOverlayOpenInner, setIsOverlayOpenInner] = useState(!!isOpen);

  const { isMobile } = useLayoutContext();

  const isMultiple = mode === SELECT_MODES.MULTIPLE;
  const hasActiveValue = Array.isArray(value) ? value.length > 0 : !!value;
  const hasExternalControl = _isBoolean(isOpen);

  const isSmall = size === 'small';
  const isOverlayOpen = hasExternalControl ? !!isOpen : isOverlayOpenInner;

  const parsedOptions = options?.length ? options : getParsedSelectOptions<Option>(children);

  const isOpenedInFullscreen = fullscreenOnMobile && isMobile && isOverlayOpen;

  const overallVirtualizationRowHeight = virtualizationRowHeight || (size === 'default' ? 48 : 36);

  useEffect(() => {
    if (hasExternalControl) {
      isOpen ? onOpen() : onClose({ outerControl: true });
    }
  }, [isOpen]);

  const handleBlur = (event: FocusEvent) => {
    if (isOpenedInFullscreen) {
      onBlur();

      return;
    }

    const { current: overlayNode } = overlayRef;
    const { current: triggerNode } = triggerRef;
    const relatedTarget = event.relatedTarget as Node;

    if (!overlayNode) {
      closeOverlayByOutsideClick();
      onBlur();
      onClose({ outsideClick: true });
    }

    if (relatedTarget && overlayNode && overlayNode.contains(relatedTarget)) {
      if (triggerRef.current && triggerRef.current.focus) {
        triggerRef.current.focus({ preventScroll: true });
      }

      return;
    }

    if (
      overlayNode &&
      triggerNode &&
      !overlayNode.contains(relatedTarget) &&
      !triggerNode.contains(relatedTarget)
    ) {
      closeOverlayByOutsideClick();
      onBlur();
      onClose({ outsideClick: true });
    }
  };

  const currentTriggerValue = useMemo(() => {
    if (Array.isArray(value)) {
      return multipleOptionsTitle;
    } else {
      const label = parsedOptions.find(
        (option) => !isOptionGroup(option) && option.value === value
      )?.label;

      return label || null;
    }
  }, [value, parsedOptions]);

  const selectOption = useCallback(
    (selectedValue: SelectSingleValue, option: Option | null, shouldClose = !isMultiple) => {
      let preparedValue: Option['value'][] = [];

      if (Array.isArray(value)) {
        if (selectedValue !== null) {
          if (value.includes(selectedValue)) {
            preparedValue = value.filter((enabledValue) => enabledValue !== selectedValue);
          } else {
            preparedValue = [...value, selectedValue];
          }
        } else {
          preparedValue = [];
        }
      }

      if (isSingleOnChange(mode, onChange)) {
        onChange(selectedValue, option);
      } else {
        onChange(preparedValue, option);
      }

      if (shouldClose) {
        onClose({ select: true });
        setIsOverlayOpenInner(false);
      }
    },
    [onChange, value, mode]
  );

  const closeOverlayByOutsideClick = useCallback(() => {
    if (!hasExternalControl) {
      onClose({ outsideClick: true });
      setIsOverlayOpenInner(false);
    }
  }, [hasExternalControl, onClose]);

  const closeOverlayByTrigger = useCallback(() => {
    if (!isDisabled) {
      onTriggerClick();

      if (!hasExternalControl) {
        onClose({ triggerClick: true });
        setIsOverlayOpenInner(false);
      }
    }
  }, [onClose, onTriggerClick, hasExternalControl, isDisabled]);

  const openOverlayByTrigger = useCallback(() => {
    if (!isDisabled) {
      onTriggerClick();

      if (!hasExternalControl) {
        onOpen();
        setIsOverlayOpenInner(true);
      }
    }
  }, [onOpen, onTriggerClick, hasExternalControl, isDisabled]);

  const renderTrigger = () => {
    const triggerOverlay = isOverlayOpen ? closeOverlayByTrigger : openOverlayByTrigger;

    return (
      <div
        className={cls(styles.triggerWrapper, childrenClassNames.triggerWrapper, {
          [styles.withResetButton]: !!withResetButton && hasActiveValue,
        })}
        ref={triggerRef}
        data-locator={`${specificLocator}-trigger`}
      >
        <button
          form={form}
          name={name}
          type="button"
          className={cls(styles.button, childrenClassNames.trigger, {
            [styles.buttonSmall]: isSmall,
            [styles.isRed]: isInvalid,
            [styles.isFocused]:
              (isOverlayOpen || isOpenedInFullscreen) && !isInvalid && !isDisabled,
            [styles.isDisabled]: isDisabled,
            [styles.isYellow]: isMultiple && hasActiveValue,
            [styles.withResetButton]: withResetButton && hasActiveValue,
            [styles.fullscreenTrigger]: isOpenedInFullscreen,
          })}
          onClick={isOpenedInFullscreen ? _noop : triggerOverlay}
          disabled={isDisabled}
          data-error-type="new"
        >
          <div className={styles.triggerInner}>
            <div
              className={cls(styles.textContainer, {
                [styles.withCornerTitle]: !!label && withTitle && currentTriggerValue && !isSmall,
              })}
            >
              {((size !== 'small' && withTitle) || !currentTriggerValue) && (
                <div
                  className={cls(styles.title, childrenClassNames.label, {
                    [styles.baseFont]: isSmall,
                    [styles.isDisabled]: isDisabled,
                    [styles.cornerTitle]: !!currentTriggerValue,
                  })}
                >
                  {label}
                </div>
              )}
              <div
                data-locator={`${specificLocator}-value`}
                className={cls(styles.text, childrenClassNames.inputContent, {
                  [styles.baseFont]: isSmall,
                })}
              >
                {currentTriggerValue}
              </div>
            </div>
            <ChevronDownIcon
              className={cls(styles.arrow, childrenClassNames.arrow, {
                [styles.isOpen]: isOverlayOpen,
                [styles.isDisabled]: isDisabled,
                [styles.arrowWithMargin]: isMultiple && hasActiveValue && !isSmall,
              })}
            />
          </div>
        </button>
        {withResetButton && (
          <div
            className={cls(styles.resetButton, {
              [styles.visible]: hasActiveValue && isMultiple,
              [styles.isYellow]: hasActiveValue && isMultiple,
              [styles.small]: isSmall,
              [styles.isFocused]: isOverlayOpen,
            })}
            onClick={() => {
              onReset();
              selectOption(null, null);
            }}
            data-locator={`${specificLocator}-reset-button`}
          >
            <CrossIcon />
          </div>
        )}
      </div>
    );
  };

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (hasExternalControl || CustomTriggerRenderer) {
        return;
      }

      const key = event.key;

      switch (KEY_VALUES[key]) {
        case KEYS.SPACE:
        case KEYS.ENTER:
          const isEnter = KEY_VALUES[key] === KEYS.ENTER;

          event.preventDefault();
          isOverlayOpenInner && onClose({ enter: isEnter, space: !isEnter });
          setIsOverlayOpenInner(!isOverlayOpenInner);
          break;
        case KEYS.TAB:
          isOverlayOpenInner && onClose({ tab: true });
          setIsOverlayOpenInner(false);
          break;
        case KEYS.ESCAPE:
          isOverlayOpenInner && onClose({ escape: true });
          setIsOverlayOpenInner(false);
          break;
        default:
          break;
      }
    },
    [value, options, children, isOverlayOpenInner, hasExternalControl]
  );

  const overlayChildrenClassNames = useMemo(
    () => ({
      content: childrenClassNames.overlayContent,
      optionWrapper: childrenClassNames.optionWrapper,
      contentWrapper: childrenClassNames.contentWrapper,
    }),
    [
      childrenClassNames.optionWrapper,
      childrenClassNames.overlayContent,
      childrenClassNames.contentWrapper,
    ]
  );

  const triggerNode = CustomTriggerRenderer ? (
    <CustomTriggerRenderer
      openOverlay={isOpenedInFullscreen ? _noop : openOverlayByTrigger}
      closeOverlay={isOpenedInFullscreen ? _noop : closeOverlayByTrigger}
      setRef={triggerRef}
      value={value}
      isOverlayOpen={isOverlayOpen}
      triggerProps={customTriggerProps}
    />
  ) : (
    renderTrigger()
  );

  if (isOpenedInFullscreen) {
    return (
      <Modal
        onClose={closeOverlayByTrigger}
        isOpen={true}
        fullScreenOnMobile={true}
        specificLocators={specificLocator}
        contentOptions={{
          className: styles.modalContent,
          childrenClassNames: {
            body: styles.modalBody,
          },
          withoutPadding: true,
        }}
      >
        {{
          header: (
            <div>
              <div className={styles.modalTitle}>{modalTitle}</div>
              {/*@ts-ignore */}
              <div onBlur={handleBlur} tabIndex={-1}>
                {triggerNode}
              </div>
            </div>
          ),
          body: (
            <Overlay
              CustomOptionRenderer={CustomOptionRenderer}
              alignType={alignType}
              childrenClassNames={overlayChildrenClassNames}
              hasExternalControl={hasExternalControl}
              isInFullscreenMode={true}
              isMultiple={isMultiple}
              isOpen={true}
              isVirtualized={isVirtualized}
              virtualizationRowHeight={overallVirtualizationRowHeight}
              isVerticalAutoPlacementDisabled={true}
              onSelect={selectOption}
              options={parsedOptions}
              placement={PLACEMENT.BOTTOM}
              ref={overlayRef}
              shouldSetInitialHover={shouldSetInitialHover}
              size="default"
              specificLocator={specificLocator}
              triggerRef={triggerRef}
              value={value}
              container={container}
            />
          ),
        }}
      </Modal>
    );
  }

  return (
    <div
      className={cls(styles.container, className)}
      data-locator={`${specificLocator}-container`}
      onKeyDown={onKeyDown}
      tabIndex={-1}
      //@ts-ignore
      onBlur={handleBlur}
    >
      {triggerNode}
      <Overlay
        CustomOptionRenderer={CustomOptionRenderer}
        alignType={alignType}
        childrenClassNames={overlayChildrenClassNames}
        hasExternalControl={hasExternalControl}
        isMultiple={isMultiple}
        isOpen={isOverlayOpen}
        isVerticalAutoPlacementDisabled={isVerticalAutoPlacementDisabled}
        onSelect={selectOption}
        options={parsedOptions}
        placement={PLACEMENT.BOTTOM}
        ref={overlayRef}
        shouldLimitHeightByScreen={shouldLimitHeightByScreen}
        shouldSetInitialHover={shouldSetInitialHover}
        isVirtualized={isVirtualized}
        virtualizationRowHeight={overallVirtualizationRowHeight}
        size={size}
        specificLocator={specificLocator}
        triggerRef={triggerRef}
        value={value}
        container={container}
      />
    </div>
  );
}

export const Option = <Option extends Omit<SelectOption, 'label'>>(
  props: Option & { children: string }
) => null;
export const OptionGroup = (props: {
  children: ReactElement | ReactElement[];
  label: string;
  className?: string;
}) => null;

export default Select;
